import { useEffect, useRef } from 'react';
import { AppState } from 'react-native';
import type {
  EquipmentDefault,
  Exercise,
  LocationUnavailable,
  PlanStarter,
} from '@heraion/shared';

import type {
  LoggerDraft,
  LoggerDispatch,
  WeightUnit,
} from './state/loggerDraft';
import { assembleDraftKeepIds } from './state/loggerTimeline';
import { withSavedNotes } from './timelineFeed';
import type { WorkoutState } from './state/loggerTimelineTypes';
import {
  hasCommittedWork,
  isNonTrivialDraft,
  shouldPersistDraft,
} from './state/loggerDraftPersistence';
import {
  clearPersistedLoggerState,
  loadPersistedLoggerState,
  savePersistedLoggerState,
  setAsideLiveDraft,
} from './state/loggerDraftStorage';
import { ensureRecentWorkoutsCached } from './useEntryHistory';
import { getLastKnownPlace } from './silentInstance';
import { useStarterHydrationOptions } from './useStarterHydrationOptions';
import type { ProgramApiFetch } from '../ProgramEditor/types';
import {
  hydrateFromPlanStarter,
  hydrateFromWorkout,
} from './workoutMapping';
import { sessionKeyOf, type LoggerMode } from './loggerMode';
import type { EditingTarget } from './LoggerScreen';
import type { ConfirmFn } from '../seams';

// Owns the Logger's draft hydration + AsyncStorage mirroring + the sticky
// place a plan-started session opens at (seedStickyPlace, below). Extracted
// verbatim out of LoggerScreen (Track R). Returns the two refs the screen
// still needs elsewhere:
//   - persistedHydratedRef — read/reset by useSaveWorkout + the cancel/
//     discard close paths to gate hydration per session.
//   - draftRef — read by post-dispatch lookups (e.g. bilateral auto-set)
//     and the background-flush listener so they see the freshest draft.
//   - restoredKeyRef: sessionKey of the last restored draft (#318).
//
// GATE-CRITICAL: this hook holds the Resume/Discard cold-boot prompt and
// the mid-workout persistence that survives a background/kill cycle. Any
// change here must be smoke-tested on a real cold boot.

// EQ-1-PROGSTART — the place a started session begins at.
//
// ⭐ Antenore's rule, 2026-08-17: APPLY the last place used and SHOW it; do not
// ask. It is the sticky-place half of equipment-model.md §9.12.2 point 3, which
// was decided on 2026-08-13 and never built — every session opened on "Where?"
// and waited for a tap, so "start this program adapted to the gym you are in"
// had no gym to adapt to.
//
// ⛔ It dispatches `set_location` rather than seeding the draft directly. The
// reducer already owns the fill rule (loggerNestedReducer) — entries never told
// a place get it, entries naming a DIFFERENT place or carrying a completed set
// are left alone, which is EQ-1-SWITCH's question and not this one's. Writing
// `location` into the hydrated draft here would fill the workout field and
// leave every entry at null, forking that rule into a second, wrong copy.
//
// ⛔ Never on a RESUMED draft: it already carries the place it was logged at,
// and re-seeding would overwrite the athlete's own answer on a cold boot.
//
// ⭐ It DOES run on a blank workout since 2026-08-20. It did not, and Antenore
// found it from the other side: *"la mia palestra LGF Le Mont non esiste,
// esiste solo nel programma? perché?"* — literally true, this was plan-only, so
// a session started from scratch opened with no place. Everything the place
// gates went with it: no ★ (an election has nowhere to be stored) and no
// machine ranking. One missing call, three symptoms.
//
// ⚠️ Silent when history is cold — with no known place it seeds nothing rather
// than guessing one. That is why the blank-workout caller AWAITS
// `ensureRecentWorkoutsCached` first: the plan path is warmed by the workout
// starter before buildPlanStarter, and nobody warms this one. The helper is
// single-flight and shared with EntryHistoryProvider, so the wait costs no
// extra GET.
const seedStickyPlace = (dispatch: LoggerDispatch): void => {
  const last = getLastKnownPlace();
  if (last === null) return;
  dispatch({ type: 'set_location', location: last });
};
type UseDraftPersistenceArgs = {
  visible: boolean;
  editing: EditingTarget | null;
  /** The mode contract. `editing` above stays for the HYDRATION payload
   *  (workout, patterns) — a different concern from what mode gates. */
  mode: LoggerMode;
  planStarter: PlanStarter | null;
  catalog: Exercise[] | null;
  /** EQ-1 — needed to WARM the recent-history window before seeding the sticky
   *  place on a blank workout. Single-flight and shared with
   *  EntryHistoryProvider, so it never costs a second GET. */
  apiFetch: ProgramApiFetch;
  /** EQ-1-PROGSTART-SILENT: the athlete's declared absences and elected
   *  machines, so a plan-started entry gets the same silent instance as add. */
  locationUnavailable?: LocationUnavailable;
  equipmentDefault?: EquipmentDefault;
  defaultUnit: WeightUnit;
  draft: LoggerDraft;
  // The raw flat store. Persisted (via its id-carrying projection) so set
  // ids survive a cold boot; all gate checks stay on the clean `draft`.
  workoutState: WorkoutState;
  /** ⭐ LG-53 — true from the moment the workout has POSTed until the athlete
   *  dismisses the summary card (`postSaveSummary !== null`). The modal stays
   *  visible through that whole phase, so without this the two writers below
   *  would keep mirroring a draft that is ALREADY on the server, and the
   *  cold-boot path would offer to resume it into a duplicate session.
   *  ⛔ Not a new persisted field: `savePersistedLoggerState` rebuilds the
   *  blob's four keys by hand (and its `.web.ts` sibling does it again), so a
   *  flag added to the stored shape would be dropped on the next write. The
   *  fact lives in memory, where it already existed. */
  postSaved: boolean;
  dispatch: LoggerDispatch;
  t: (k: string, opts?: Record<string, unknown>) => string;
  setError: (v: string | null) => void;
  setServerError: (v: string | null) => void;
  confirm: ConfirmFn;
};

export function useDraftPersistence({
  visible,
  editing,
  mode,
  planStarter,
  catalog,
  apiFetch,
  locationUnavailable,
  equipmentDefault,
  defaultUnit,
  draft,
  workoutState,
  postSaved,
  dispatch,
  t,
  setError,
  setServerError,
  confirm,
}: UseDraftPersistenceArgs) {
  // Session-scoped hydrate gate. True once we've hydrated the draft
  // from somewhere (edit-mode server workout, plan-starter, or
  // AsyncStorage probe) for this Logger session. Stays true across
  // minimize → restore so the in-memory reducer is the source of truth
  // and a stale AsyncStorage read can't clobber sets the user typed
  // before minimizing (inbox 614e2cb7). Reset to false only in the
  // close paths (cancel / discard / save success) right next to
  // dispatch({type:'reset'}) so the next session starts fresh.
  //
  // Ref instead of state on purpose: ref writes don't trigger
  // re-renders or effect re-fires, so the gate flip in close paths
  // can't trip the hydrate / save effects mid-shutdown. The save
  // effect reads it on each fire (driven by draft changes).
  const persistedHydratedRef = useRef(false);
  // #318: set before the restoring dispatch.
  const restoredKeyRef = useRef<string | null>(null);

  // The rounding (LG-15), the saved load shapes and the silent machine (EQ-1)
  // a plan starter hydrates with: one source, shared with «More time» (#356).
  const starterOptions = useStarterHydrationOptions({ locationUnavailable, equipmentDefault });

  // Hydrate or reset the draft on visibility transitions.
  //
  // Edit mode: hydrate from the server-side workout on first open of
  // the session. Subsequent visible flips (minimize → restore) reuse
  // the in-memory reducer state so unsaved local edits survive.
  //
  // New-workout mode: try AsyncStorage on first open — if the user
  // backgrounded the app mid-workout, the persisted draft is brought
  // back. Only fall through to a fresh reducer state when no
  // persisted draft exists.
  //
  // We deliberately do NOT dispatch reset on visible=false: the user
  // minimizing or closing the app must not wipe a live session.
  // Explicit discard / save paths clear AsyncStorage and dispatch
  // reset themselves.
  useEffect(() => {
    if (visible && editing && !persistedHydratedRef.current) {
      dispatch({
        type: 'hydrate',
        draft: withSavedNotes(
          hydrateFromWorkout(
            editing.workout,
            defaultUnit,
            editing.patternsBySlug ?? {},
            editing.mode ?? 'edit',
            catalog,
          ),
          editing,
        ),
      });
      persistedHydratedRef.current = true;
      return;
    }
    if (visible && planStarter && !persistedHydratedRef.current) {
      // Plan-starter hydration resolves isBodyweight per entry from the
      // catalog FK (no denormalized snapshot — see
      // feedback_no_denormalized_display_snapshots). The catalog loads
      // cache-first one async tick after the modal opens, so on the
      // FIRST commit it is still null. Hydrating against a null catalog
      // downgrades every entry to weighted: a pure-bodyweight prescribed
      // set (weightKg=0) then renders an empty weight, and buildPayload
      // silently drops the whole entry on save (Bug B, 2026-05-26 —
      // reverse-hyperextension + captain's-chair-leg-raises vanished).
      // Wait for a real catalog instead: `catalog` is in this effect's
      // dep array, so the effect re-fires when it flips null→loaded and
      // hydrates correctly then. We must NOT flip persistedHydratedRef
      // until that happens, or the re-fire would no-op against the gate.
      // The catalog is cache-first, so this only blocks until the
      // AsyncStorage read returns — safe offline as long as a prior
      // session cached the catalog.
      if (catalog === null) return;
      let cancelledPlan = false;
      void (async () => {
        // ⭐ LG-76 — RESUME beats SEED. Since LG-74 the starter outlives the
        // navigator remount an experience switch performs; the reducer below it
        // does not, so a plan session coming BACK lands here rather than on the
        // AsyncStorage branch, and the reducer guard below was answering about
        // the copy the remount had just destroyed.
        //
        // ⛔ Not a copy of that branch: it asks "is there anything at all", to
        // resume a blank workout. This asks "is there COMMITTED work", to
        // decide whether a program may seed over one.
        const persisted = await loadPersistedLoggerState();
        if (cancelledPlan) return;
        // Silent on purpose: useLoggerHandoff refuses a starter over a live
        // workout, so committed work HERE means a return, not a start —
        // prompting would raise a dialog on every experience round trip.
        if (persisted !== null && hasCommittedWork(persisted.draft)) {
          restoredKeyRef.current = sessionKeyOf(persisted.draft, null, 'log');
          dispatch({ type: 'hydrate', draft: persisted.draft });
          persistedHydratedRef.current = true;
          return;
        }
        // One read of the saved shapes for both seeds below.
        const hydrateOpts = await starterOptions(planStarter.entries.map((e) => e.exerciseId));
        if (cancelledPlan) return;
        // Belt-and-suspenders: a new planStarter arrives while committed work is
        // in the REDUCER (race between the WorkoutsListScreen guard and React
        // batching). ⚠️ Still reads `draft`, not the slot — the probe above has
        // spoken for that copy, so reaching here means the work really is in
        // memory, which is what keeps "keep the current workout" a valid no-op.
        if (hasCommittedWork(draft)) {
          const discard = await confirm({
            title: t('mobile.logger.conflictTitle'),
            body: t('mobile.logger.conflictBody'),
            confirmLabel: t('mobile.logger.resumeDiscard'),
            cancelLabel: t('mobile.logger.discardCancel'),
          });
          if (cancelledPlan) return;
          // Set aside, not destroyed: the live draft leaves its slot only
          // once the copy reads back (#288). A failed copy keeps the current
          // workout as it is, as if the athlete had kept it.
          const asideOk = discard && (await setAsideLiveDraft());
          if (cancelledPlan) return;
          if (discard && !asideOk) {
            console.warn('[logger] current workout not set aside: the plan does not replace it');
          }
          if (asideOk) {
            dispatch({ type: 'reset' });
            dispatch({
              type: 'hydrate',
              draft: hydrateFromPlanStarter(
                planStarter,
                defaultUnit,
                catalog,
                hydrateOpts,
              ),
            });
            seedStickyPlace(dispatch);
          }
          // Either choice resolves the conflict for this session — keep
          // the current workout (cancel) or replace it (discard). Mark
          // hydrated so this branch doesn't re-fire on the next render.
          persistedHydratedRef.current = true;
          return;
        }
        // Plan-starter hydrate runs once per session, against the loaded
        // catalog so isBodyweight resolves correctly (see the gate above).
        // The Plan tab already did the buildPlanStarter math against the
        // freshest MAXes; we trust the prescribed values here.
        dispatch({
          type: 'hydrate',
          draft: hydrateFromPlanStarter(
            planStarter,
            defaultUnit,
            catalog,
            hydrateOpts,
          ),
        });
        seedStickyPlace(dispatch);
        persistedHydratedRef.current = true;
      })();
      return () => {
        cancelledPlan = true;
      };
    }
    if (visible && !editing && !planStarter && !persistedHydratedRef.current) {
      let cancelled = false;
      void (async () => {
        const persisted = await loadPersistedLoggerState();
        if (cancelled) return;
        if (persisted && isNonTrivialDraft(persisted.draft)) {
          restoredKeyRef.current = sessionKeyOf(persisted.draft, null, 'log');
          dispatch({ type: 'hydrate', draft: persisted.draft });
          persistedHydratedRef.current = true;
          return;
        }
        // ⛔ The mirror is ungated BEFORE the place is sought, never after.
        // `persistedHydratedRef` is what releases the draft→AsyncStorage
        // mirror below, and the warm-up under it can take seconds against a
        // slow or erroring backend (apiRetry backs off up to 4 times). Sets
        // typed in that window would not be persisted — and a place is a
        // convenience while losing logged sets is the one thing that must not
        // happen. Caught in review before it shipped.
        persistedHydratedRef.current = true;
        // A genuinely blank workout: apply the last place, same rule the plan
        // path follows. ⛔ Only here, never over a resumed draft.
        await ensureRecentWorkoutsCached(apiFetch);
        if (cancelled) return;
        seedStickyPlace(dispatch);
      })();
      return () => {
        cancelled = true;
      };
    }
    if (!visible) {
      setError(null);
      setServerError(null);
    }
    // Hydration must fire only on visibility / mode transitions, never on
    // every `draft` keystroke (that would re-run the AsyncStorage probe
    // and clobber live edits). `draft` is read through the gate ref, and
    // setError/setServerError/t are stable. Deps preserved verbatim from
    // the pre-extraction LoggerScreen effect.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, editing, planStarter, catalog, dispatch, defaultUnit, apiFetch]);

  // #213: edit, repeat and a draft saved before the flag can be open before the
  // catalog is, with `isAssisted` not known. Completing on arrival (rather than
  // waiting for the catalog, as the plan branch does) keeps edit/repeat opening
  // at once and offline, and one effect covers all three. Blank sets only.
  useEffect(() => {
    if (catalog && draft.entries.some((e) => e.isAssisted === undefined)) {
      dispatch({ type: 'complete_assisted', catalog });
    }
  }, [catalog, draft, dispatch]);

  // Mirror the live draft into AsyncStorage on every change so a
  // background/kill/remount cycle still rehydrates the in-progress workout.
  // Persists new-workout AND repeat drafts (both unsaved, in-memory-only
  // otherwise — both are mode 'log'); skips true edit, which is server-backed
  // (shouldPersistDraft).
  const draftRef = useRef(draft);
  draftRef.current = draft;
  // Mirror the flat store too, so the background-flush listener (a stale
  // closure) serializes the freshest id-carrying draft.
  const workoutStateRef = useRef(workoutState);
  workoutStateRef.current = workoutState;
  useEffect(() => {
    if (!visible) return;
    if (!shouldPersistDraft(mode)) return;
    if (!persistedHydratedRef.current) return;
    // ⭐ LG-53 — the workout is on the server; this draft is a receipt, not
    // unsaved work. Mirroring it back would undo the clear the save path
    // just did and hand the cold-boot prompt a resumable duplicate.
    if (postSaved) return;
    if (isNonTrivialDraft(draft)) {
      // Persist the id-carrying projection (assembleDraftKeepIds) so set ids
      // survive a cold boot — flattenDraft reuses them on hydrate. An older
      // id-less persisted draft mints fresh ids, so the back-compat shim is
      // automatic. Gate checks stay on the clean `draft`.
      void savePersistedLoggerState(assembleDraftKeepIds(workoutState), true);
    } else {
      // The user emptied the draft (last entry removed). Clear the
      // persisted slot so a fresh open lands on a clean form.
      void clearPersistedLoggerState();
    }
  }, [visible, mode, draft, workoutState, postSaved]);

  // Flush on background — even though the change-effect above already
  // wrote on every keystroke, an explicit write at the lifecycle edge
  // is cheap insurance against a kill that lands between a setState and
  // the next render.
  useEffect(() => {
    if (!visible) return;
    if (!shouldPersistDraft(mode)) return;
    // ⭐ LG-53 — gated with the change-effect above, and this is the half that
    // matches the real crash: backgrounding the app while the summary card is
    // up is exactly how a process gets killed before the athlete taps Done.
    // A flush here would re-write the slot the save path just cleared.
    if (postSaved) return;
    const sub = AppState.addEventListener('change', (next) => {
      if (next === 'background' || next === 'inactive') {
        const current = draftRef.current;
        if (isNonTrivialDraft(current)) {
          void savePersistedLoggerState(
            assembleDraftKeepIds(workoutStateRef.current),
            true,
          );
        }
      }
    });
    return () => sub.remove();
  }, [visible, mode, postSaved]);

  return { persistedHydratedRef, draftRef, restoredKeyRef };
}
